import { useCallback, useContext, useEffect, useState } from 'react';

import { getEmaCurationsStatus, updateEmaCurationsStatus } from '../../../api/ema';
import Store from '../../../store';
import Actions from '../../../store/actions';
import { EmaCurationsStatus, EmaCurationsStatusApiResponse } from '../../../types/dtos/ema';
import HttpStatus from '../../../types/enum/http_status';
import {
  EmaCurationsAlertColor,
  EmaCurationsStatusStoreValue
} from './EmaCurationsStatusToggle.types';

const isEmaCurationsStatus = (status: unknown): status is EmaCurationsStatus =>
  status === 'processing' || status === 'done';

const isSuccessfulResponse = (response: EmaCurationsStatusApiResponse) =>
  response?.status === HttpStatus.OK && response.data?.status === HttpStatus.OK;

const getResponseMessage = (response: EmaCurationsStatusApiResponse, fallback: string) => {
  const { body, message } = response?.data ?? {};

  if (message) {
    return message;
  }
  if (typeof body === 'string') {
    return body;
  }
  return body?.message ?? fallback;
};

const getExceptionMessage = (exception: unknown, fallback: string) =>
  exception instanceof Error ? exception.message : fallback;

const useEmaCurationsStatus = () => {
  const { dispatch } = useContext(Store) as unknown as EmaCurationsStatusStoreValue;
  const [currentStatus, setCurrentStatus] = useState<EmaCurationsStatus | ''>('');
  const [pendingStatus, setPendingStatus] = useState<EmaCurationsStatus | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);

  const showAlert = useCallback(
    (message: string, color?: EmaCurationsAlertColor) => {
      dispatch({
        type: Actions.SET_ALERT,
        value: { status: true, message, ...(color && { color }) }
      });
    },
    [dispatch]
  );

  useEffect(() => {
    let isMounted = true;

    const fetchStatus = async () => {
      try {
        const response = await getEmaCurationsStatus();
        const status =
          typeof response?.data?.body === 'object' ? response.data.body.status : undefined;

        if (!isMounted) {
          return;
        }
        if (!isSuccessfulResponse(response) || !isEmaCurationsStatus(status)) {
          showAlert(getResponseMessage(response, 'Unable to load the EMA curation status'));
          return;
        }

        setCurrentStatus(status);
      } catch (exception) {
        if (isMounted) {
          showAlert(getExceptionMessage(exception, 'Unable to load the EMA curation status'));
        }
      }
    };

    fetchStatus();

    return () => {
      isMounted = false;
    };
  }, [showAlert]);

  const selectStatus = useCallback(
    (status: EmaCurationsStatus | null) => {
      if (status && status !== currentStatus && !isUpdating) {
        setPendingStatus(status);
      }
    },
    [currentStatus, isUpdating]
  );

  const closeDialog = useCallback(() => {
    if (!isUpdating) {
      setPendingStatus(null);
    }
  }, [isUpdating]);

  const confirmStatusChange = useCallback(async () => {
    if (!pendingStatus || isUpdating) {
      return;
    }

    const nextStatus = pendingStatus;
    setIsUpdating(true);

    try {
      const response = await updateEmaCurationsStatus(nextStatus);

      if (!isSuccessfulResponse(response)) {
        showAlert(getResponseMessage(response, 'Unable to update the EMA curation status'));
        return;
      }

      setCurrentStatus(nextStatus);
      showAlert(`Success: Curation status updated to ${nextStatus.toUpperCase()}`, 'success');
    } catch (exception) {
      showAlert(getExceptionMessage(exception, 'Unable to update the EMA curation status'));
    } finally {
      setIsUpdating(false);
      setPendingStatus(null);
    }
  }, [isUpdating, pendingStatus, showAlert]);

  return {
    currentStatus,
    pendingStatus,
    isUpdating,
    selectStatus,
    closeDialog,
    confirmStatusChange
  };
};

export default useEmaCurationsStatus;
